/**
 * AuthContext.tsx - Authentication Context and Provider
 * 
 * Provides centralized authentication state and ensures token is available
 * before components attempt to load data
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/authService';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing token on mount
    const existingToken = authService.getToken();
    
    if (existingToken) {
      setToken(existingToken);
      setIsAuthenticated(true);
    }
    
    setIsLoading(false);

    // Listen for custom auth login event
    const handleAuthLogin = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newToken = customEvent.detail?.token || authService.getToken();
      if (newToken) {
        setToken(newToken);
        setIsAuthenticated(true);
      }
    };

    window.addEventListener('authLogin', handleAuthLogin);
    return () => window.removeEventListener('authLogin', handleAuthLogin);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
